import { Project, ProjectSettingsModeGroupEnum, Subproject } from '@/app/api';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ProjectTabs } from '@/app/pages/ProjectDetail/types';
import { Tab } from '@/app/components/Tabs/Tabs';
import { formatNumber } from '@/app/utils/formatting';

type UseTabsProps = {
  project: Project;
  projectNewsLength?: number;
  subProjectsCount?: number;
  subProject?: Subproject;
};

interface TabWithVisibilityProperty<TabIdType> extends Tab<TabIdType> {
  isHidden?: boolean;
}

export const useTabs = ({ project, projectNewsLength, subProjectsCount, subProject }: UseTabsProps) => {
  const { t } = useTranslation();
  const donationsCount = project.statistics?.donationsCount ?? 0;

  const commercialProjectTabs: TabWithVisibilityProperty<ProjectTabs>[] = useMemo(
    () => [
      { id: ProjectTabs.PROJECT, label: t('pages.collection.commercialProjectTab') },
      {
        id: ProjectTabs.NEWS,
        label: t('pages.collection.news', {
          count: projectNewsLength,
          countFormatted: formatNumber(projectNewsLength ?? 0),
        }),
        isHidden: !projectNewsLength,
      },
      {
        id: ProjectTabs.DONORS,
        label: t('pages.collection.commercialProjectDonorsTab', {
          count: project.statistics?.donationsCount ?? 0,
          countFormatted: formatNumber(project.statistics?.donationsCount ?? 0),
        }),
      },
      { id: ProjectTabs.FAQS, label: t('pages.collection.faqTab') },
    ],
    [projectNewsLength, project],
  );

  const dobroProjectTabs: TabWithVisibilityProperty<ProjectTabs>[] = useMemo(
    () =>
      [
        { id: ProjectTabs.PROJECT, label: t('pages.collection.collectionTab') },
        {
          id: ProjectTabs.NEWS,
          label: t('pages.collection.news', {
            count: projectNewsLength,
            countFormatted: formatNumber(projectNewsLength ?? 0),
          }),
          isHidden: !projectNewsLength,
        },
        {
          id: ProjectTabs.DONORS,
          label: donationsCount
            ? t('pages.collection.donorsTabWithCount', {
                count: project.statistics?.donationsCount ?? 0,
                countFormatted: formatNumber(project.statistics?.donationsCount ?? 0),
              })
            : t('pages.collection.donorsTabWithoutCount'),
        },
        {
          id: ProjectTabs.SUBPROJECT,
          label: subProjectsCount
            ? t('pages.collection.challengeTabWithCount', {
                count: subProjectsCount,
                countFormatted: formatNumber(subProjectsCount),
              })
            : t('pages.collection.challengeTabWithoutCount'),
        },
        { id: ProjectTabs.FAQS, label: t('pages.collection.faqTab') },
      ].filter((tab) => !tab.isHidden),
    [subProjectsCount, projectNewsLength, project],
  );

  const subProjectTabs: TabWithVisibilityProperty<ProjectTabs>[] = useMemo(() => {
    return [
      { id: ProjectTabs.PROJECT, label: t('pages.collection.collectionTab') },
      {
        id: ProjectTabs.DONORS,
        label: subProject?.statistics.donationsCount
          ? t('pages.collection.donorsTabWithCount', {
              count: subProject?.statistics.donationsCount ?? 0,
              countFormatted: formatNumber(subProject?.statistics.donationsCount ?? 0),
            })
          : t('pages.collection.donorsTabWithoutCount'),
      },
      { id: ProjectTabs.FAQS, label: t('pages.collection.faqTab') },
    ];
  }, [t, subProject]);

  if (subProject) {
    return subProjectTabs.filter((tab) => !tab.isHidden);
  }

  if (project.settings.modeGroup === ProjectSettingsModeGroupEnum.Plus) {
    return commercialProjectTabs.filter((tab) => !tab.isHidden);
  }

  return dobroProjectTabs.filter((tab) => !tab.isHidden);
};
